import { InlineNotification } from '@carbon/react';
import { ChoiceDropdown, DateRangeFilter, detailRoute, PageHeader, useFilters, useFormat, useReferenceOptions } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { hoApi } from '../api';
import { dayRange, setOnly } from '../logs/dates';
import { MultiChoice } from '../logs/fields';
import { LogTable, type LogQuery } from '../logs/LogTable';

type Entry = HoComponents['schemas']['PointsLedgerRow'];

const TYPES = ['earn', 'reverse', 'redeem', 'refund', 'adjust', 'reward', 'expire'] as const;

/**
 * Loyalty › Member points (ADR 0044): every member's points movements, newest first. Points are earned and redeemed by HO
 * from the orders stores send; an adjustment is made on the member's account. Search finds an account, card or order by its
 * number; the total is counted up to a limit, as the ledger runs to millions of entries (ADR 0045).
 */
export function MemberPointsPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const navigate = useNavigate();
  const stores = useReferenceOptions('org.store');
  const filters = useFilters({ type: '', store: '', from: '', to: '' });
  const [capped, setCapped] = useState(false);

  const storeName = (id: string | null | undefined) => (id ? (stores.data?.find((s) => s.value === id)?.label ?? '') : '');
  const typeName = (type: string) => t(`loyalty.points.types.${type}` as I18nKey, { defaultValue: type });
  const signed = (n: number | string) => (Number(n) === 0 ? '' : `${Number(n) > 0 ? '+' : ''}${format.number(n)}`);

  const load = async (q: LogQuery) => {
    const { type, store, from, to } = filters.applied;
    const { data, error } = await hoApi.GET('/api/v1/membership/points-entries', {
      params: { query: { ...setOnly({ type, storeId: store, ...dayRange(from, to) }), ...q } },
    });
    if (!data) throw error;
    setCapped(Boolean(data.totalCapped));
    return data;
  };

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('loyalty.history.title')} description={t('loyalty.history.hint')} />
      {capped && (
        <InlineNotification kind="info" lowContrast hideCloseButton title={t('loyalty.history.capped')} />
      )}
      <LogTable<Entry>
        label={t('loyalty.history.title')}
        queryKey={['ho', 'member-points']}
        load={load}
        rowId={(e) => e.id}
        filters={filters}
        filterFields={
          <>
            <MultiChoice
              id="points-type"
              label={t('loyalty.points.columns.type')}
              choices={TYPES.map((type) => ({ id: type, label: typeName(type) }))}
              value={filters.draft.type}
              onChange={(v) => filters.set('type', v)}
            />
            <ChoiceDropdown
              id="points-store"
              label={t('loyalty.history.store')}
              choices={(stores.data ?? []).map((s) => ({ id: s.value, label: s.label }))}
              value={filters.draft.store}
              onChange={(v) => filters.set('store', v)}
            />
            <DateRangeFilter
              id="points-date"
              legendText={t('loyalty.points.columns.when')}
              from={filters.draft.from}
              to={filters.draft.to}
              onChange={(r) => filters.setDraft((d) => ({ ...d, ...r }))}
            />
          </>
        }
        onOpen={(e) => void navigate(detailRoute('membership.member_account', e.accountId))}
        columns={[
          { key: 'when', header: t('loyalty.points.columns.when'), render: (e) => format.dateTime(e.occurredAt) },
          { key: 'account', header: t('loyalty.history.account'), render: (e) => e.accountNo },
          { key: 'type', header: t('loyalty.points.columns.type'), render: (e) => typeName(e.entryType) },
          {
            key: 'points',
            header: t('loyalty.points.columns.points'),
            render: (e) => `${Number(e.points) > 0 ? '+' : ''}${format.number(e.points)}`,
          },
          { key: 'balance', header: t('loyalty.points.columns.balance'), render: (e) => format.number(e.balanceAfter) },
          { key: 'tier', header: t('loyalty.points.columns.tierPoints'), render: (e) => signed(e.tierPoints) },
          { key: 'order', header: t('loyalty.history.order'), render: (e) => e.orderNumber ?? '' },
          { key: 'store', header: t('loyalty.history.store'), render: (e) => storeName(e.storeId) },
          { key: 'note', header: t('loyalty.points.columns.note'), render: (e) => (e.orderId ? '' : (e.note ?? '')) },
        ]}
      />
    </section>
  );
}
